import { AUTH_URL } from 'src/configs';
import request from 'src/request';

export type DashboardDuration = 'daily' | 'weekly' | 'monthly' | 'yearly' | 'all_time';

export type StatusCount = {
  _id: string;
  count: number;
};

export type PeriodCount = {
  _id: string;
  count: number | Record<string, number>;
};

export type DepositDashboard = {
  total_count: number;
  deposit_transaction_count: StatusCount[];
};

export type WithdrawDashboard = {
  total_count: number;
  withdraw_transaction_count: StatusCount[];
};

export type UsersDashboard = {
  total_count: number;
  user_count: PeriodCount[];
};

export type OrdersDashboard = {
  total_count: number;
  orders_count: StatusCount[];
};

export type KycDashboard = {
  total_count: number;
  kyc_count: PeriodCount[];
};

export type BankAccountsDashboard = {
  total_count: number;
  bank_accounts_count: StatusCount[];
};

export type TransactionsDashboard = {
  transaction_count: PeriodCount[];
};

const dashboardGet = async <T>(path: string, duration: DashboardDuration): Promise<T> => {
  // Query string, not a body: browsers drop the body of a GET, so `duration`
  // never reached the API and every card was stuck on the monthly default.
  const response = await request.get(`${AUTH_URL}/v1/admin/dashboard/${path}`, {
    params: { duration },
  });
  return response.data as T;
};

export const fetchDepositDashboard = (duration: DashboardDuration) =>
  dashboardGet<DepositDashboard>('transactions/deposit', duration);

export const fetchWithdrawDashboard = (duration: DashboardDuration) =>
  dashboardGet<WithdrawDashboard>('transactions/withdraw', duration);

export const fetchUsersDashboard = (duration: DashboardDuration) =>
  dashboardGet<UsersDashboard>('users', duration);

export const fetchOrdersDashboard = (duration: DashboardDuration) =>
  dashboardGet<OrdersDashboard>('orders', duration);

export const fetchKycDashboard = (duration: DashboardDuration) =>
  dashboardGet<KycDashboard>('kyc', duration);

export const fetchBankAccountsDashboard = (duration: DashboardDuration) =>
  dashboardGet<BankAccountsDashboard>('bank-accounts', duration);

export const fetchTransactionsDashboard = (duration: DashboardDuration) =>
  dashboardGet<TransactionsDashboard>('transactions', duration);

export const DURATION_OPTIONS: { value: DashboardDuration; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'all_time', label: 'All time' },
];

export const formatDashboardNumber = (value: number | null | undefined) => {
  if (value == null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('en-US').format(value);
};

export const sumStatusCounts = (items?: StatusCount[]) =>
  (items || []).reduce((sum, item) => sum + (Number(item.count) || 0), 0);

export const periodValue = (count: PeriodCount['count']): number => {
  if (typeof count === 'number') return count;
  if (count && typeof count === 'object') {
    return Object.values(count).reduce((sum, n) => sum + (Number(n) || 0), 0);
  }
  return 0;
};

const MONTH_ORDER = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export const sortPeriodCounts = (items: PeriodCount[], duration: DashboardDuration): PeriodCount[] => {
  const copy = [...(items || [])];
  if (duration === 'monthly') {
    return copy.sort(
      (a, b) => MONTH_ORDER.indexOf(String(a._id).toLowerCase()) - MONTH_ORDER.indexOf(String(b._id).toLowerCase()),
    );
  }
  if (duration === 'daily') {
    return copy.sort(
      (a, b) => DAY_ORDER.indexOf(String(a._id).toLowerCase()) - DAY_ORDER.indexOf(String(b._id).toLowerCase()),
    );
  }
  if (duration === 'yearly') {
    return copy.sort((a, b) => Number(a._id) - Number(b._id));
  }
  if (duration === 'weekly') {
    return copy.sort((a, b) => {
      const an = Number(String(a._id).replace(/\D/g, '')) || 0;
      const bn = Number(String(b._id).replace(/\D/g, '')) || 0;
      return bn - an;
    });
  }
  return copy;
};

export const formatPeriodLabel = (id: string) => {
  const raw = String(id || '');
  if (/^\d{4}$/.test(raw)) return raw;
  if (raw.endsWith('d')) return raw;
  return raw.charAt(0).toUpperCase() + raw.slice(1, 3);
};
